import { addClassNamesToElement, isHTMLAnchorElement } from "@lexical/utils";
import {
  $applyNodeReplacement,
  $isElementNode,
  $isRangeSelection,
  DOMConversionMap,
  DOMConversionOutput,
  EditorConfig,
  ElementNode,
  GridSelection,
  LexicalNode,
  NodeKey,
  NodeSelection,
  RangeSelection,
  SerializedElementNode,
  Spread,
} from "lexical";

export type SerializedMentionNode = Spread<
  {
    modelType: string;
    id: string;
    slug: string;
    text: string;
  },
  SerializedElementNode
>;

export class MentionNode extends ElementNode {
  __modelType: string;
  __slug: string;
  __id: string;
  __text: string;

  static getType(): string {
    return "mention";
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(
      node.__modelType,
      node.__id,
      node.__slug,
      node.__text,
      node.__key
    );
  }

  constructor(
    modelType: string,
    id: string,
    slug: string,
    text: string,
    key?: NodeKey
  ) {
    super(key);
    this.__modelType = modelType;
    this.__id = id;
    this.__slug = slug;
    this.__text = text;
  }

  createDOM(config: EditorConfig): HTMLAnchorElement {
    const type = this.__modelType;
    const slug = this.__slug;
    const id = this.__id;
    const text = this.__text;

    let url = "";
    let citation = "";
    if (
      [
        "accounts",
        "companies",
        "insight-providers",
        "Account",
        "Company",
        "factset-institution-people",
      ].includes(type)
    ) {
      url = `/${
        type === "accounts" ||
        type === "Account" ||
        type === "insight-providers"
          ? "profiles"
          : "companies"
      }/${slug}`;
      citation = `${
        type === "accounts" ||
        type === "Account" ||
        type === "insight-providers"
          ? "accounts"
          : "companies"
      }:${id}`;
    }
    if (type === "entities") {
      url = `/entities/${slug}`;
      citation = `entities:${id}`;
    }
    if (type === "insights") {
      url = `/insights/${slug}`;
      citation = `insights:${id}`;
    }
    if (type === "factset-institution-people") {
      url = `/targeting?investor=${id}`;
      citation = `factset-institution-people:${id}`;
    }
    // Define the DOM element here
    const element = document.createElement("a");
    // element.innerText = text;
    element.href = url;
    element.setAttribute("title", text);
    element.setAttribute("rel", "noopener");
    element.setAttribute("target", "_blank");
    element.setAttribute("data-smartkarma-citation", citation);

    element.setAttribute("class", "editor-link ltr");
    element.setAttribute("dir", "ltr");

    addClassNamesToElement(element, config.theme.mention);
    return element;
  }

  updateDOM(_prevNode: MentionNode, element: HTMLAnchorElement): boolean {
    const type = this.__modelType;
    const slug = this.__slug;
    const id = this.__id;
    const text = this.__text;

    let url = "";
    let citation = "";

    if (
      [
        "accounts",
        "companies",
        "insight-providers",
        "Account",
        "Company",
        "factset-institution-people",
      ].includes(type)
    ) {
      url = `/${
        type === "accounts" ||
        type === "Account" ||
        type === "insight-providers"
          ? "profiles"
          : "companies"
      }/${slug}`;
      citation = `${
        type === "accounts" ||
        type === "Account" ||
        type === "insight-providers"
          ? "accounts"
          : "companies"
      }:${id}`;
    }
    if (type === "entities") {
      url = `/entities/${slug}`;
      citation = `entities:${id}`;
    }
    if (type === "insights") {
      url = `/insights/${slug}`;
      citation = `insights:${id}`;
    }
    if (type === "factset-institution-people") {
      url = `/targeting?investor=${id}`;
      citation = `factset-institution-people:${id}`;
    }
    // element.innerText = text;
    element.href = url;
    element.setAttribute("title", text);
    element.setAttribute("rel", "noopener");
    element.setAttribute("target", "_blank");
    element.setAttribute("data-smartkarma-citation", citation);

    // Returning false tells Lexical that this node does not need its
    // DOM element replacing with a new copy from createDOM.
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      a: (node: HTMLElement) => ({
        conversion: convertMentionElement,
        priority: 2,
      }),
    };
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const node = $createMentionNode(
      serializedNode.modelType,
      serializedNode.id,
      serializedNode.slug,
      serializedNode.text
    );
    node.setFormat(serializedNode.format);
    node.setIndent(serializedNode.indent);
    node.setDirection(serializedNode.direction);
    return node;
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      modelType: this.getModelType(),
      id: this.getId(),
      slug: this.getSlug(),
      text: this.getText(),
      type: "mention",
      version: 1,
    };
  }

  getModelType(): string {
    return this.getLatest().__modelType;
  }

  setModelType(modelType: string): void {
    const writable = this.getWritable();
    writable.__modelType = modelType;
  }

  getId(): string {
    return this.getLatest().__id;
  }

  setId(id: string): void {
    const writable = this.getWritable();
    writable.__id = id;
  }

  getSlug(): string {
    return this.getLatest().__slug;
  }

  setSlug(slug: string): void {
    const writable = this.getWritable();
    writable.__slug = slug;
  }

  getText(): string {
    return this.getLatest().__text;
  }

  setText(text: string): void {
    const writable = this.getWritable();
    writable.__text = text;
  }

  insertNewAfter(
    selection: RangeSelection,
    restoreSelection = true
  ): null | ElementNode {
    const element = this.getParentOrThrow().insertNewAfter(
      selection,
      restoreSelection
    );
    if ($isElementNode(element)) {
      const linkNode = $createMentionNode(
        this.__modelType,
        this.__id,
        this.__slug,
        this.__text
      );
      element.append(linkNode);
      return linkNode;
    }
    return null;
  }

  canInsertTextBefore(): false {
    return false;
  }

  canInsertTextAfter(): false {
    return false;
  }

  canBeEmpty(): false {
    return false;
  }

  isInline(): true {
    return true;
  }

  extractWithChild(
    child: LexicalNode,
    selection: RangeSelection | NodeSelection | GridSelection,
    _destination: "clone" | "html"
  ): boolean {
    if (!$isRangeSelection(selection)) {
      return false;
    }

    const anchorNode = selection.anchor.getNode();
    const focusNode = selection.focus.getNode();

    return (
      this.isParentOf(anchorNode) &&
      this.isParentOf(focusNode) &&
      selection.getTextContent().length > 0
    );
  }
}

function convertMentionElement(domNode: HTMLElement): DOMConversionOutput {
  let node = null;
  if (isHTMLMentionElement(domNode)) {
    const content = domNode.textContent;
    const href = domNode.getAttribute("href");
    const hrefSplit = href?.split("/");
    const citation = domNode.getAttribute("data-smartkarma-citation");

    const type = citation?.split(":")?.[0];
    const id = citation?.split(":")?.[1];
    const slug = hrefSplit?.[hrefSplit?.length - 1];
    if (!!type && !!id && !!slug && !!content) {
      node = $createMentionNode(type, id, slug, content);
    }
  }
  return { node };
}

function isHTMLMentionElement(domNode: HTMLElement): boolean {
  return (
    isHTMLAnchorElement(domNode) &&
    !!domNode.getAttribute("data-smartkarma-citation")
  );
}

export function $createMentionNode(
  modelType: string,
  id: string,
  slug: string,
  text: string
): MentionNode {
  const mentionNode = new MentionNode(modelType, id, slug, text);
  return $applyNodeReplacement(mentionNode);
}

export function $createMentionNodeFromMention(mention: any): MentionNode {
  const mentionNode = new MentionNode(
    mention.modelType || mention.type,
    mention.modelId || mention.id,
    mention.slug,
    mention.tagline || mention.fulltext || mention.name
  );
  return $applyNodeReplacement(mentionNode);
}

export function $isMentionNode(
  node: LexicalNode | null | undefined
): node is MentionNode {
  return node instanceof MentionNode;
}
